import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface SectionModalContextValue {
  activeSectionId?: string;
  isSectionModalOpen: boolean;
  openSectionModal: (sectionId?: string) => void;
  closeSectionModal: () => void;
}

export const SectionModalContext = createContext<SectionModalContextValue | null>(null);

export const useSectionModal = () => {
  const context = useContext(SectionModalContext);

  if (!context) {
    throw new Error('"SectionModalProvider" must be present in the DOM tree');
  }

  return context;
};

export const SectionModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string>();

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);

    setTimeout(() => {
      // Account for modal exit animation
      setActiveSectionId(undefined);
    }, 200);
  }, []);

  const handleOpenModal = useCallback((sectionId?: string) => {
    setActiveSectionId(sectionId);
    setIsOpen(() => true);
  }, []);

  const value = useMemo(
    () => ({
      activeSectionId,
      closeSectionModal: handleCloseModal,
      isSectionModalOpen: isOpen,
      openSectionModal: handleOpenModal,
    }),
    [activeSectionId, handleCloseModal, handleOpenModal, isOpen],
  );

  return <SectionModalContext.Provider value={value}>{children}</SectionModalContext.Provider>;
};
